import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { useIonAlert } from '@ionic/react';
import { Browser } from '@capacitor/browser';
import { Check, Eye, FileUp, Pencil, X } from 'lucide-react';
import { Button, Card, DocumentStatusBadge, ErrorState, Notice, SectionTitle, Spinner } from '@tcrd/ui';
import { Page } from '../../../components/Page';
import { useToast } from '../../../components/toast';
import { api, errorMessage } from '../../../lib/api';
import { env } from '../../../lib/env';
import { pickFile, pickImage, toForm } from '../../../lib/media';
import { useAuth } from '../../../stores/auth';
import { daysLeftLabel, SIDE_LABEL, type DocumentDto } from './types';

type Side = keyof typeof SIDE_LABEL;

export function DocumentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const toast = useToast();
  const [alert] = useIonAlert();
  const isAdmin = useAuth((s) => s.hasRole('TENANT_ADMIN'));
  const [busy, setBusy] = useState<string | null>(null);
  const q = useQuery({ queryKey: ['document', id], queryFn: () => api<DocumentDto>(`/documents/${id}`) });

  const refresh = () =>
    Promise.all(['document', 'documents', 'compliance'].map((k) => qc.invalidateQueries({ queryKey: k === 'document' ? [k, id] : [k] })));

  const upload = async (side: Side) => {
    try {
      // Licencia: fotos (cámara). Póliza/otros: imagen o PDF.
      const blob = side === 'ATTACHMENT' ? await pickFile('image/jpeg,image/png,image/webp,application/pdf') : await pickImage();
      if (!blob) return;
      setBusy(side);
      await api(`/documents/${id}/files/${side}`, { method: 'PUT', form: toForm(blob, {}, side.toLowerCase()) });
      await refresh();
      toast('Archivo guardado. Queda pendiente de verificación.', 'success');
    } catch (e) {
      toast(errorMessage(e), 'danger');
    } finally {
      setBusy(null);
    }
  };

  const view = async (side: Side) => {
    try {
      const { url } = await api<{ url: string }>(`/documents/${id}/files/${side}/url`);
      if (env.isNative) await Browser.open({ url });
      else window.open(url, '_blank', 'noopener');
    } catch (e) {
      toast(errorMessage(e), 'danger');
    }
  };

  const verify = async (decision: 'VERIFIED' | 'REJECTED', reason?: string) => {
    setBusy(decision);
    try {
      await api(`/documents/${id}/verify`, { body: { decision, reason } });
      await refresh();
      toast(decision === 'VERIFIED' ? 'Documento verificado' : 'Documento rechazado', decision === 'VERIFIED' ? 'success' : 'dark');
    } catch (e) {
      toast(errorMessage(e), 'danger');
    } finally {
      setBusy(null);
    }
  };

  const d = q.data;
  const sides: Side[] = d?.type.code === 'DRIVER_LICENSE' ? ['FRONT', 'BACK'] : ['ATTACHMENT'];

  return (
    <Page
      title={d?.type.name ?? 'Documento'}
      back="/driver/documents"
      onRefresh={() => q.refetch()}
      actions={d && !d.superseded && <Link to={`/driver/documents/${id}/edit`} aria-label="Editar" className="tap flex items-center justify-center px-2"><Pencil className="size-5" /></Link>}
    >
      {q.isLoading && <Spinner />}
      {q.error && <ErrorState message={errorMessage(q.error)} onRetry={() => q.refetch()} />}
      {d && (
        <div className="flex flex-col gap-5">
          {d.superseded && <Notice>Este documento fue reemplazado por uno más reciente.</Notice>}
          {d.status === 'REJECTED' && d.rejectionReason && <Notice tone="danger">Rechazado: {d.rejectionReason}</Notice>}
          {d.status === 'PENDING_VERIFICATION' && <Notice>Pendiente de verificación por el administrador. Subir la imagen no lo verifica automáticamente.</Notice>}

          <Card className="p-5">
            <div className="flex items-center justify-between gap-3">
              <DocumentStatusBadge status={d.status} />
              {d.expiresAt && <span className="text-sm text-[var(--text-muted)]">{daysLeftLabel(d.daysLeft)}</span>}
            </div>
            <dl className="mt-4 grid grid-cols-2 gap-y-3 gap-x-4 text-sm">
              {[
                ['Número', d.number],
                ['Categoría', d.category],
                ['Emisor / aseguradora', d.issuer],
                ['Vehículo', d.vehicle?.label],
                ['Expedición', d.issuedAt],
                ['Inicio de vigencia', d.startsAt],
                ['Vencimiento', d.expiresAt],
                ['Conductor', d.driver.name],
              ]
                .filter(([, v]) => v)
                .map(([k, v]) => (
                  <div key={k}>
                    <dt className="text-[var(--text-muted)]">{k}</dt>
                    <dd className="font-medium">{v}</dd>
                  </div>
                ))}
            </dl>
          </Card>

          <section>
            <SectionTitle title="Imágenes" subtitle="Privadas y cifradas. Solo tú y el administrador pueden verlas." />
            <div className="flex flex-col gap-3">
              {sides.map((side) => {
                const has = d.files.includes(side);
                return (
                  <Card key={side} className="p-4 flex items-center gap-3">
                    <FileUp className="size-5 text-[var(--text-muted)]" aria-hidden />
                    <div className="flex-1">
                      <p className="font-medium">{SIDE_LABEL[side]}</p>
                      <p className="text-xs text-[var(--text-muted)]">{has ? 'Archivo cargado' : 'Sin archivo'}</p>
                    </div>
                    {has && <Button size="sm" variant="ghost" icon={<Eye className="size-4" />} onClick={() => view(side)}>Ver</Button>}
                    {!d.superseded && <Button size="sm" variant="secondary" loading={busy === side} onClick={() => upload(side)}>{has ? 'Reemplazar' : 'Subir'}</Button>}
                  </Card>
                );
              })}
            </div>
          </section>

          {isAdmin && !d.superseded && (
            <Card className="p-5">
              <SectionTitle title="Verificación" subtitle="Revisa las imágenes antes de verificar." />
              <div className="flex gap-3">
                <Button variant="accent" icon={<Check className="size-5" />} loading={busy === 'VERIFIED'} disabled={!d.files.length} onClick={() => verify('VERIFIED')}>Verificar</Button>
                <Button
                  variant="danger"
                  icon={<X className="size-5" />}
                  loading={busy === 'REJECTED'}
                  onClick={() =>
                    alert({
                      header: 'Rechazar documento',
                      inputs: [{ name: 'reason', type: 'textarea', placeholder: 'Motivo (se envía al conductor)' }],
                      buttons: [
                        { text: 'Cancelar', role: 'cancel' },
                        { text: 'Rechazar', handler: (v: { reason: string }) => void verify('REJECTED', v.reason) },
                      ],
                    })
                  }
                >
                  Rechazar
                </Button>
              </div>
              {d.verifications && d.verifications.length > 0 && (
                <ul className="mt-4 text-sm text-[var(--text-muted)] flex flex-col gap-1">
                  {d.verifications.map((v) => (
                    <li key={v.id}>
                      {new Date(v.createdAt).toLocaleString('es-DO')} — {v.decision === 'VERIFIED' ? 'Verificado' : `Rechazado: ${v.reason}`}
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          )}
        </div>
      )}
    </Page>
  );
}
